// ===========================================
// COMPONENTE: ProrrogaDialog
// ===========================================
// Dialog para gestionar prórrogas fiscales: listar existentes + crear nueva

import { useState, useMemo } from 'react'
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CalendarClock, Loader2, Plus, ArrowLeft, Trash2 } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { useCatalogoImpuestos } from '@/hooks/useCatalogoImpuestos'
import { useProrrogas, useEliminarProrroga, useVencimientosCalendario } from '@/hooks/useCalendario'
import { useProrrogaForm, getFieldError, type ProrrogaFormValues } from './forms'
import { etiquetaVencimiento } from './etiquetaVencimiento'
import type { ProrrogaInsert, Prorroga } from '@/types/vencimientos'
import { useStore } from '@tanstack/react-form'
import { FormDialogContent } from '@/components/ui/form-dialog-content'

interface ProrrogaDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirmar: (datos: ProrrogaInsert) => Promise<void>
  organizacionId?: string | null
}

export function ProrrogaDialog({
  open,
  onOpenChange,
  onConfirmar,
  organizacionId,
}: ProrrogaDialogProps) {
  const [vista, setVista] = useState<'lista' | 'crear'>('lista')
  const [isSubmitting, setIsSubmitting] = useState(false)
  // ORR-277: el impuesto elegido alimenta el selector de vencimientos; `vencimientoId` es la
  // fila del calendario elegida, de la que salen período, fecha original y terminación.
  const [impuestoSeleccionado, setImpuestoSeleccionado] = useState<string | null>(null)
  const [vencimientoId, setVencimientoId] = useState('')

  const { catalogo, isLoading: catalogoLoading } = useCatalogoImpuestos()
  const { data: prorrogas = [], isLoading: prorrogasLoading } = useProrrogas(organizacionId)
  const { data: vencimientosCalendario = [], isLoading: vencimientosLoading } =
    useVencimientosCalendario(impuestoSeleccionado)
  const eliminarProrroga = useEliminarProrroga()

  const impuestosActivos = useMemo(
    () => catalogo.filter((c) => c.activo),
    [catalogo],
  )

  // Map impuesto_id → nombre for display
  const impuestoNombreMap = useMemo(() => {
    const map = new Map<string, string>()
    catalogo.forEach((c) => map.set(c.id, c.nombre))
    return map
  }, [catalogo])

  const prorrogasActivas = useMemo(
    () => (prorrogas as Prorroga[]).filter((p) => p.activo),
    [prorrogas],
  )

  const { form } = useProrrogaForm({
    onSubmit: async (value: ProrrogaFormValues) => {
      setIsSubmitting(true)
      try {
        await onConfirmar({
          impuesto_id: value.impuesto_id,
          periodo: value.periodo,
          terminacion_cuit: value.terminacion_cuit || null,
          fecha_original: value.fecha_original,
          fecha_nueva: value.fecha_nueva,
          motivo: value.motivo,
          fuente: value.fuente || null,
          referencia: value.referencia || null,
        })
        resetFormulario()
        setVista('lista')
      } catch (error) {
        console.error('Error al crear prórroga:', error)
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  /** Limpia el formulario junto con la selección de impuesto y vencimiento. */
  const resetFormulario = () => {
    form.reset()
    setImpuestoSeleccionado(null)
    setVencimientoId('')
  }

  const isDirty = useStore(form.store, (s) => s.isDirty)

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetFormulario()
      setVista('lista')
    }
    onOpenChange(newOpen)
  }

  const handleEliminar = async (id: string) => {
    await eliminarProrroga.mutateAsync(id)
  }

  const formatFecha = (fecha: string) => {
    try {
      return format(parseISO(fecha), "d 'de' MMM yyyy", { locale: es })
    } catch {
      return fecha
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <FormDialogContent
        isDirty={isDirty}
        onDiscard={() => handleOpenChange(false)} className="sm:max-w-[560px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-orange-600" />
            Prórrogas Fiscales
          </DialogTitle>
          <DialogDescription>
            {vista === 'lista'
              ? 'Prórrogas activas que modifican las fechas de vencimiento'
              : 'Registrar una nueva prórroga o extensión de fecha'}
          </DialogDescription>
        </DialogHeader>

        {vista === 'lista' ? (
          /* =================== VISTA: LISTA =================== */
          <div className="flex flex-col gap-4 flex-1 min-h-0">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setVista('crear')}>
                <Plus className="h-4 w-4 mr-2" />
                Nueva Prórroga
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
              {prorrogasLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Cargando...
                </div>
              ) : prorrogasActivas.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarClock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No hay prórrogas activas</p>
                  <p className="text-xs mt-1">
                    Las prórrogas modifican automáticamente las fechas de vencimiento en el calendario
                  </p>
                </div>
              ) : (
                prorrogasActivas.map((prorroga) => (
                  <div
                    key={prorroga.id}
                    className="border rounded-lg p-3 space-y-2"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">
                            {impuestoNombreMap.get(prorroga.impuesto_id) ?? 'Impuesto'}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {prorroga.periodo}
                          </Badge>
                          {prorroga.terminacion_cuit && (
                            <Badge variant="secondary" className="text-xs">
                              CUIT {prorroga.terminacion_cuit}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                          <span className="line-through">{formatFecha(prorroga.fecha_original)}</span>
                          <span>→</span>
                          <span className="font-medium text-foreground">{formatFecha(prorroga.fecha_nueva)}</span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => handleEliminar(prorroga.id)}
                        disabled={eliminarProrroga.isPending}
                        title="Desactivar prórroga"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">{prorroga.motivo}</p>
                    {prorroga.referencia && (
                      <p className="text-xs text-muted-foreground italic">{prorroga.referencia}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
          /* =================== VISTA: CREAR =================== */
          <div className="flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto">
            <Button
              variant="ghost"
              size="sm"
              className="self-start -ml-2"
              onClick={() => { resetFormulario(); setVista('lista') }}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Volver a la lista
            </Button>

            <form
              onSubmit={(e) => {
                e.preventDefault()
                form.handleSubmit()
              }}
              className="space-y-4"
            >
              {/* Impuesto */}
              <form.Field name="impuesto_id">
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor={field.name}>Impuesto *</Label>
                    <Select
                      value={field.state.value || ''}
                      onValueChange={(val) => {
                        field.handleChange(val)
                        setImpuestoSeleccionado(val)
                        // Cambiar de impuesto invalida el vencimiento elegido: sin esto quedarían
                        // los valores del impuesto anterior y se guardaría una prórroga cruzada.
                        setVencimientoId('')
                        form.setFieldValue('periodo', '')
                        form.setFieldValue('fecha_original', '')
                        form.setFieldValue('terminacion_cuit', null)
                      }}
                      disabled={catalogoLoading}
                    >
                      <SelectTrigger id={field.name}>
                        <SelectValue placeholder="Seleccionar impuesto" />
                      </SelectTrigger>
                      <SelectContent>
                        {impuestosActivos.map((imp) => (
                          <SelectItem key={imp.id} value={imp.id}>
                            {imp.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {getFieldError(field.state.meta.errors)}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              {/* Vencimiento a prorrogar — reemplaza Período + Terminación CUIT + Fecha Original.
                  Los tres salen de la fila elegida, que es contra la que después matchea la
                  prórroga. Tipearlos a mano hacía imposible acertar el período (ORR-277). */}
              <form.Field name="fecha_original">
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="vencimiento-prorrogar">Vencimiento a prorrogar *</Label>
                    <Select
                      value={vencimientoId}
                      onValueChange={(val) => {
                        const opcion = vencimientosCalendario.find((v) => v.id === val)
                        if (!opcion) return
                        setVencimientoId(val)
                        form.setFieldValue('periodo', opcion.periodo)
                        form.setFieldValue('fecha_original', opcion.fecha_vencimiento)
                        form.setFieldValue('terminacion_cuit', opcion.terminacion_cuit)
                      }}
                      disabled={!impuestoSeleccionado || vencimientosLoading}
                    >
                      <SelectTrigger id="vencimiento-prorrogar">
                        <SelectValue
                          placeholder={
                            !impuestoSeleccionado
                              ? 'Elegí primero el impuesto'
                              : vencimientosLoading
                                ? 'Cargando vencimientos...'
                                : 'Seleccionar vencimiento'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {vencimientosCalendario.map((v) => (
                          <SelectItem key={v.id} value={v.id}>
                            {etiquetaVencimiento(v)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {impuestoSeleccionado && !vencimientosLoading && vencimientosCalendario.length === 0 && (
                      <p className="text-sm text-muted-foreground">
                        Este impuesto no tiene vencimientos cargados en el calendario.
                      </p>
                    )}
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        Elegí el vencimiento que querés prorrogar
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              {/* Fecha nueva */}
              <div>
                <form.Field name="fecha_nueva">
                  {(field) => (
                    <div className="space-y-2">
                      <Label htmlFor={field.name}>Fecha Nueva *</Label>
                      <Input
                        id={field.name}
                        type="date"
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-sm text-destructive">
                          {getFieldError(field.state.meta.errors)}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>
              </div>

              {/* Motivo */}
              <form.Field name="motivo">
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor={field.name}>Motivo *</Label>
                    <Textarea
                      id={field.name}
                      placeholder="Ej: Prórroga por feriado nacional, RG AFIP 5678/2026..."
                      className="resize-none"
                      rows={2}
                      value={field.state.value || ''}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {getFieldError(field.state.meta.errors)}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              {/* Fuente y Referencia */}
              <div className="grid grid-cols-2 gap-4">
                <form.Field name="fuente">
                  {(field) => (
                    <div className="space-y-2">
                      <Label htmlFor={field.name}>Fuente</Label>
                      <Select
                        value={field.state.value || 'manual'}
                        onValueChange={(val) => field.handleChange(val)}
                      >
                        <SelectTrigger id={field.name}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">Manual</SelectItem>
                          <SelectItem value="rg_afip">RG AFIP</SelectItem>
                          <SelectItem value="decreto">Decreto</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </form.Field>

                <form.Field name="referencia">
                  {(field) => (
                    <div className="space-y-2">
                      <Label htmlFor={field.name}>Referencia</Label>
                      <Input
                        id={field.name}
                        placeholder="Ej: RG 5678/2026"
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                    </div>
                  )}
                </form.Field>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { resetFormulario(); setVista('lista') }}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Crear Prórroga
                </Button>
              </div>
            </form>
          </div>
        )}
      </FormDialogContent>
    </Dialog>
  )
}
